import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useEffect, useState } from 'react';
import { useRootStore } from '../hooks/useRootStore';
import { Client } from '../data/Client';
import { Project } from '../data/Project';
import useDeviceMedia from '../hooks/useDeviceMedia';

export default function ChangeProjectDialog({ issueId, project, onSubmit, onCancel }: { issueId: number, project: Project | null, onSubmit: (changed: Project) => void, onCancel: () => void }) {

    const [changed, setChanged] = useState(project);
    const { isMobilePortrait } = useDeviceMedia();

    const [clientId, setClientId] = useState(project?.ClientID);
    const [clients, setClients] = useState<Client[]>([] as Client[]);
    const [projects, setProjects] = useState<Project[]>([] as Project[]);

    const [clientNotSpecified, setClientNotSpecified] = useState(false);
    const [projectNotSpecified, setProjectNotSpecified] = useState(false);

    const { api } = useRootStore();

    useEffect(() => {
        api.getClients().then(clients => {
            setClients(clients);            
            if (clients.length == 1 && clients[0].ID != clientId) {
                setClientId(clients[0].ID);
            }
        });
    }, [api]);

    const check = (): boolean => {
        let result = true;

        if (!clientId) {
            setClientNotSpecified(true);
            result = false;
        }
        if (!changed) {
            setProjectNotSpecified(true);
            result = false;
        }

        return result;
    }

    useEffect(() => {        
        const client = clients.filter(x => x.ID === clientId)[0] ?? null;
        if (client) {
            const clientProjects = client.Projects;
            clientProjects.forEach(p => {
                p.ClientID = client.ID;
                p.ClientName = client.Name;
            })
            setProjects(clientProjects);
            const activeProject = clientProjects.filter(p => p.ID == changed?.ID)[0];
            if (activeProject) {
                setChanged(activeProject);
            } else {
                setChanged(null);
            }
        }
        else {
            setProjects([] as Project[]);
        };
    }, [clients, clientId]);

    const changeProjectID = async () => {
        if (check()) {
            await api.updateIssueProjectID(issueId, changed!.ID)
                .then(() => onSubmit(changed!));
        }
    }

    const handleClientIDChange = (newID: number) => {
        setClientNotSpecified(false);
        const client = clients.filter(x => x.ID == newID)[0];
        const clientProjects = client.Projects;
        clientProjects.forEach(p => {
            p.ClientID = newID;
            p.ClientName = client.Name;
        })
        setProjects(clientProjects);
        setClientId(newID);
        if (clientProjects.length == 1) {
            setProjectNotSpecified(false);
            setChanged(clientProjects[0]);
        }
        else {
            setChanged(null);
        }
    }

    const handleProjectIDChange = (proj: Project | null) => {
        setProjectNotSpecified(proj == null);
        setChanged(proj);
    }

    return (
        <Dialog
            maxWidth='md'
            fullWidth
            open={true}
            onClose={onCancel}
        >
            <DialogTitle>Изменение проекта</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', mb: 3, flexDirection: isMobilePortrait ? 'column' : 'row' }}>
                    <FormControl
                        sx={{ mt: 1, mr: 1, width: "100%", minWidth: 120 }}
                        error={clientNotSpecified}
                    >
                        <InputLabel>Клиент</InputLabel>
                        <Select
                            label='Клиент'
                            id="demo-simple-select-disabled"
                            value={clientId ?? ''}
                            required
                            disabled={clients.length < 2}
                            onChange={(e) => handleClientIDChange(e.target.value as number)}
                        >
                            {clients.map((c) => <MenuItem key={c.ID} value={c.ID}>{c.Name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl
                        sx={{ mt: isMobilePortrait ? 3 : 1, width: "100%", minWidth: 120 }}
                        error={projectNotSpecified}
                    >
                        <InputLabel id="demo-simple-select-label">Проект</InputLabel>
                        <Select
                            id="demo-simple-select-disabled"
                            defaultValue={0}
                            value={changed?.ID ?? ''}
                            label='Проект'
                            required
                            onChange={(e) => {
                                const ps = projects.filter(p => p.ID == e.target.value as number);                                
                                handleProjectIDChange(ps.length > 0 ? ps[0] : null);
                            }}
                            disabled={(projects?.length ?? 0) < 2}
                        >
                            {projects.map((p) => <MenuItem key={p.ID} value={p.ID}>{p.Name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Отмена</Button>
                <Button onClick={changeProjectID}>Изменить проект</Button>
            </DialogActions>
        </Dialog>
    );
}